import { buildUserAgent, WportHttpError } from '@wport/core';
import * as transport from '@wport/core';
import type {
	EnterpriseRequestOptions as TransportRequestOptions,
	EnterpriseWriteExtra,
	EnterpriseGetResult,
	EnterprisePostResult,
} from '@wport/core';
import { CLI_SOURCE } from './global-opts';
import { printWarn } from './output';

export type { EnterpriseWriteExtra, EnterpriseGetResult, EnterprisePostResult };

/**
 * CLI 呼叫端仍用這個 4 欄位形狀（不含 `userAgent`）——wrapper 自己補 `userAgent`
 * 再轉呼叫 transport，命令端組 opts 的地方不用改。
 */
export type EnterpriseRequestOptions = Omit<TransportRequestOptions, 'userAgent'>;

function withUserAgent(opts: EnterpriseRequestOptions): TransportRequestOptions {
	return { ...opts, userAgent: buildUserAgent('wport-cli', __CLI_VERSION__), source: CLI_SOURCE };
}

/**
 * spec §5 + pm_41 §3：401/403 附情境提示；400 帶 publish gate missing_fields 時列出缺欄。
 *
 * ⚠️ 為何是「合併提示」而非按子類精準分流（2026-07-01 staging live smoke 實測校正）：
 * 後端全域 `I18nExceptionFilter` 會把 guard 丟的 `{ path: 'error.enterprise.expired_api_key' }`
 * 翻成 `message` 後**丟掉 path**，實際 body 只有 `{ message(已翻譯), error, statusCode }`。
 * 過期 / 撤銷 / 無效在 client 端**同為 401、無機器可辨識訊號**（訊息是 DB i18n、5 語系、會改字，
 * 不可 regex）。所以無法「拆開給不同 next-step」，改給一則涵蓋兩種修法的提示。
 *
 * 這仍解掉 backlog §3.3 的核心痛點：舊版只寫「run login」，過期 key 重 login 仍 401 是死路；
 * 現在明確點出「過期 → keys rotate（過期 key 仍可 rotate）」這條出路。
 * 精準分流需後端在錯誤契約放穩定 `code`（全域 filter 變更），列為後端 follow-up。
 *
 * 提示一律單行：頂層 printError 走 sanitizeForTerminal 會剝掉 \n。
 *
 * 回傳**新的 `WportHttpError`**（保留原 `status`/`body`，非 `CliError`）——沿用 `WportHttpError`
 * 而非裸 `WportError` 是刻意的：頂層 `exitCodeForError` 靠 `instanceof WportHttpError` + `status`
 * 判斷 exit code（401/403/400 仍要落在 3），裸 `WportError` 會落到預設的 4，改變對外 exit code 契約。
 */
/**
 * spec §3.1.2／§3.2.4 的五個新 error_code（block 四區塊整段取代 + descriptions 影片連結）。
 * 判斷優先序固定為 `error_code` > HTTP status —— 這五碼在 body 頂層 additive 出現時，
 * 不管 HTTP status 是什麼（含既有 401/403/400 已有專屬提示的 status），一律優先用這裡的
 * 訊息；`message` 隨 locale 變動不可靠，只認 `error_code`。
 */
interface EnterpriseErrorCodeBody {
	error_code: 'block_max_exceeded' | 'unsupported_video_provider' | 'block_item_forbidden' | 'profile_revision_conflict' | 'profile_revision_required';
	block?: string;
	limit?: number;
	current_revision?: string;
}

function extractErrorCodeBody(body: unknown): EnterpriseErrorCodeBody | undefined {
	if (!body || typeof body !== 'object') return undefined;
	const b = body as Record<string, unknown>;
	const code = b.error_code;
	if (
		code !== 'block_max_exceeded' &&
		code !== 'unsupported_video_provider' &&
		code !== 'block_item_forbidden' &&
		code !== 'profile_revision_conflict' &&
		code !== 'profile_revision_required'
	) {
		return undefined;
	}
	return {
		error_code: code,
		block: typeof b.block === 'string' ? b.block : undefined,
		limit: typeof b.limit === 'number' ? b.limit : undefined,
		current_revision: typeof b.current_revision === 'string' ? b.current_revision : undefined,
	};
}

/** spec §3.2.4：五個 error_code 各自的裝飾訊息。回傳 `undefined` 代表這不是本次案子關心的 code。 */
function decorateByErrorCode(base: string, err: WportHttpError, info: EnterpriseErrorCodeBody): WportHttpError {
	switch (info.error_code) {
		case 'block_max_exceeded':
			return new WportHttpError(
				`${base} — Block "${info.block ?? 'unknown'}" exceeds its item limit` +
					(info.limit !== undefined ? ` (max ${info.limit})` : '') +
					'. Run `wport enterprise company view --output json` to see the current items before replacing.',
				err.status,
				err.body
			);
		case 'unsupported_video_provider':
			return new WportHttpError(
				`${base} — Unsupported video URL. Accepted forms: ` +
					'https://www.youtube.com/watch?v=<id>, https://youtu.be/<id>, ' +
					'https://vimeo.com/<id>, https://player.vimeo.com/video/<id>.',
				err.status,
				err.body
			);
		case 'block_item_forbidden':
			return new WportHttpError(
				`${base} — This enc_id (block "${info.block ?? 'unknown'}") does not belong to your company or no longer exists. ` +
					'Run `wport enterprise company view` to get the latest enc_id list.',
				err.status,
				err.body
			);
		case 'profile_revision_conflict':
			return new WportHttpError(
				`${base} — Block "${info.block ?? 'unknown'}" changed since you last read it` +
					(info.current_revision ? ` (current revision: ${info.current_revision})` : '') +
					'. This CLI will not auto-retry: re-run `wport enterprise company view` to see the latest state, then confirm again.',
				err.status,
				err.body
			);
		case 'profile_revision_required':
			return new WportHttpError(
				`${base} — This request needs an If-Match revision (block "${info.block ?? 'unknown'}") and none was recognized. ` +
					'This usually means the CLI is out of date — upgrade it, or re-run `wport enterprise company view` to fetch a fresh revision before retrying.',
				err.status,
				err.body
			);
	}
}

function decorateEnterpriseError(err: unknown): unknown {
	if (!(err instanceof WportHttpError)) return err;
	const base = err.message;
	const errorCodeInfo = extractErrorCodeBody(err.body);
	if (errorCodeInfo) return decorateByErrorCode(base, err, errorCodeInfo);
	if (err.status === 401) {
		return new WportHttpError(
			`${base} — If your key has expired, rotate it in place: \`wport enterprise keys rotate <enc_id>\` ` +
				'(an expired key is still accepted for rotate). ' +
				'If it was revoked or is incorrect, obtain a valid key and run `wport enterprise login`.',
			err.status,
			err.body
		);
	}
	if (err.status === 403) {
		return new WportHttpError(
			`${base} — Your key may lack the required scope; rotate or issue a key that includes it. ` +
				'If your company account has been suspended, please contact support.',
			err.status,
			err.body
		);
	}
	if (err.status === 400) {
		// issue #106 P2：publish gate 失敗回 `data.missing_fields`，但翻譯後的 message 只有中文一句話，
		// 不含欄位清單 → agent 不知道缺哪些。把結構化 missing_fields 明列進錯誤訊息（消費端可 parse）。
		const missingFields = extractMissingFields(err.body);
		if (missingFields.length > 0) {
			return new WportHttpError(
				`${base} — Missing required fields: ${missingFields.join(', ')}. ` +
					'Fill them via `wport enterprise jobs update <enc_id> ...` (or the web console), then publish.',
				err.status,
				err.body
			);
		}
	}
	return err;
}

/**
 * 從錯誤 body 取 publish gate 的 `data.missing_fields`（string[]）；缺席 / 型別不符回 []。
 * 契約來源：`validate-job-completeness-for-publish.rule.ts` 丟
 * `BadRequestException({ path, data: { missing_fields } })`，全域 i18n filter 保留 `data`。
 */
function extractMissingFields(body: unknown): string[] {
	if (!body || typeof body !== 'object') return [];
	const data = (body as { data?: unknown }).data;
	if (!data || typeof data !== 'object') return [];
	const fields = (data as { missing_fields?: unknown }).missing_fields;
	if (!Array.isArray(fields)) return [];
	return fields.filter((f): f is string => typeof f === 'string');
}

/** spec §5：剩餘配額 <10% 時 stderr 提醒（不阻斷；stderr 不污染 json stdout）。 */
function warnIfRateLimitLow(headers: Headers): void {
	const remaining = Number(headers.get('x-ratelimit-remaining'));
	const limit = Number(headers.get('x-ratelimit-limit'));
	if (Number.isFinite(remaining) && Number.isFinite(limit) && limit > 0 && remaining / limit < 0.1) {
		printWarn(`Rate limit nearly exhausted: ${remaining}/${limit} requests remaining this window.`, false);
	}
}

async function wrap<T extends EnterpriseGetResult | EnterprisePostResult>(p: Promise<T>): Promise<T> {
	let res: T;
	try {
		res = await p;
	} catch (err) {
		throw decorateEnterpriseError(err);
	}
	warnIfRateLimitLow(res.headers);
	return res;
}

export function enterpriseGet(
	opts: EnterpriseRequestOptions,
	path: string,
	query?: Record<string, string | number | undefined>
): Promise<EnterpriseGetResult> {
	return wrap(transport.enterpriseGet(withUserAgent(opts), path, query));
}

export function enterprisePost(
	opts: EnterpriseRequestOptions,
	path: string,
	body: Record<string, unknown>,
	extra?: EnterpriseWriteExtra
): Promise<EnterprisePostResult> {
	return wrap(transport.enterprisePost(withUserAgent(opts), path, body, extra));
}

export function enterprisePatch(
	opts: EnterpriseRequestOptions,
	path: string,
	body: Record<string, unknown>,
	extra?: EnterpriseWriteExtra
): Promise<EnterprisePostResult> {
	return wrap(transport.enterprisePatch(withUserAgent(opts), path, body, extra));
}

/** 企業 API 寫入 PUT（company profile 四區塊整段取代）。四支端點皆強制 Idempotency-Key + If-Match。 */
export function enterprisePut(
	opts: EnterpriseRequestOptions,
	path: string,
	body: Record<string, unknown>,
	extra?: EnterpriseWriteExtra
): Promise<EnterprisePostResult> {
	return wrap(transport.enterprisePut(withUserAgent(opts), path, body, extra));
}

export function enterpriseDelete(
	opts: EnterpriseRequestOptions,
	path: string,
	extra?: EnterpriseWriteExtra
): Promise<EnterprisePostResult> {
	return wrap(transport.enterpriseDelete(withUserAgent(opts), path, extra));
}
